import { resolveImsLoginUrl, resolveImsRegisterUrl } from '~/utils/imsRuntime'
import { getRuntimeAuthKeys } from '~/composables/useStorageKey'
import { getAuthCookieOptions } from '~/utils/storageKey'

/**
 * @description 认证链接组合函数，用于统一管理登录和注册链接
 * @returns {Object} 登录和注册链接
 */
export const useAuthLinks = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const appBaseURL = config.app.baseURL || '/'
  const keys = getRuntimeAuthKeys()
  /**
   * @description 获取登录链接
   * @returns {string} 登录链接地址
   */
  const getLoginUrl = () => {
    return resolveImsLoginUrl(config.public)
  }

  /**
   * @description 获取注册链接
   * @returns {string} 注册链接地址
   */
  const getRegisterUrl = () => {
    return resolveImsRegisterUrl(config.public)
  }

  /**
   * @description 内部统一处理跳转认证页面的逻辑
   * @param {string} baseUrl - 登录或注册的基础 URL
   * @param {string} [redirect_url] - 默认的回调地址
   */
  const performAuthNavigation = (baseUrl, redirect_url = null) => {
    if (import.meta.client) {
      // 获取 Nuxt 内部的相对路径（不含 app.baseURL）
      const currentPath = route.path
      const currentRelativeUrl = route.fullPath

      // 确定给到外部系统的回调地址
      let targetUrl = ''

      // 如果当前不是首页，则把当前页记录到缓存，回调地址强制改为首页
      // 这样登录完成后会先回到首页（处理 token），再从首页跳转回原始页
      if (currentPath !== '/' && currentPath !== '') {
        const redirectBackCookie = useCookie(keys.redirectBackCookie, getAuthCookieOptions())
        // 存储当前页面的 Nuxt 相对路径
        redirectBackCookie.value = currentRelativeUrl
        // 给外部系统的回调地址固定为首页（带完整的 appBaseURL）
        targetUrl = window.location.origin + appBaseURL
      } else {
        // 如果是在首页点，则优先使用传入的回调地址，没有则用当前相对路径
        targetUrl = redirect_url || currentRelativeUrl
        // 如果是相对路径，转为绝对路径传给成都（需包含 appBaseURL）
        if (typeof targetUrl === 'string' && !targetUrl.startsWith('http')) {
          // 确保 targetUrl 以 / 开头
          const normalizedPath = targetUrl.startsWith('/') ? targetUrl : '/' + targetUrl
          // 如果目标地址没有包含 appBaseURL，则手动补全
          const fullPath = normalizedPath.startsWith(appBaseURL)
            ? normalizedPath
            : appBaseURL +
              (normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath)
          targetUrl = window.location.origin + fullPath
        }
      }

      const url = `${baseUrl}?redirect_url=${encodeURIComponent(targetUrl)}`
      // console.log('[useAuthLinks] 正在跳转到认证页面:', url)
      window.location.href = url
    }
  }

  /**
   * @description 跳转到登录页面
   * @param {string} [redirect_url] - 登录后跳转的地址（可选，默认为当前页面）
   */
  const navigateToLogin = (redirect_url = null) => {
    performAuthNavigation(getLoginUrl(), redirect_url)
  }

  /**
   * @description 跳转到注册页面
   * @param {string} [redirect_url] - 注册后跳转的地址（可选，默认为当前页面）
   */
  const navigateToRegister = (redirect_url = null) => {
    performAuthNavigation(getRegisterUrl(), redirect_url)
  }

  /**
   * @description 处理认证失败（如 token 过期），统一跳转
   * @reason 在跳转前需要清理本地登录态信息
   * @param {Object} [options]
   * @param {boolean} [options.forceRedirect] - 是否强制跳转（默认 false；公共页面默认不跳转，避免打断浏览）
   * @param {boolean} [options.redirectToLogin] - 是否跳转外部登录页并保留当前页面回跳
   * @param {string} [options.redirectTo] - 强制/允许跳转时的目标地址（默认 '/'）
   */
  const handleUnauthorized = async (options = {}) => {
    if (import.meta.client) {
      const userStore = useUserStore()
      // 清理本地登录态（不调用退出接口，因为此时 token 可能已失效）
      await userStore.logout(true)

      const redirectTo = typeof options.redirectTo === 'string' ? options.redirectTo : '/'
      const forceRedirect = options.forceRedirect === true
      const redirectToLogin = options.redirectToLogin === true

      // 公共页面不强制跳转：避免“闪一下又回首页”（例如商品详情、商品列表等游客可访问页面）
      // 受限页面仍会通过页面自身的 middleware / 业务逻辑触发跳转。
      let currentPath = ''
      try {
        const route = useRoute()
        currentPath = route?.path || ''
      } catch (e) {
        currentPath = window?.location?.pathname || ''
      }

      const publicRoutes = ['/', '/website', '/store', '/login', '/register']
      const publicRoutePrefixes = ['/store/products']
      const isPublicRoute =
        publicRoutes.includes(currentPath) ||
        publicRoutePrefixes.some(
          (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`)
        )

      if (redirectToLogin && (forceRedirect || !isPublicRoute)) {
        navigateToLogin()
        return
      }

      if (forceRedirect || !isPublicRoute) {
        navigateTo(redirectTo)
      }
    }
  }

  return {
    getLoginUrl,
    getRegisterUrl,
    navigateToLogin,
    navigateToRegister,
    handleUnauthorized,
  }
}
